import React, { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const DateRangePicker = ({ startDate, endDate, onChange }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleApply = () => {
    onChange(tempStartDate, tempEndDate);
    setShowPicker(false);
  };

  const handleCancel = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setShowPicker(false);
  };

  const handleDateClick = (date) => {
    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      setTempStartDate(date);
      setTempEndDate(null);
    } else if (date < tempStartDate) {
      setTempEndDate(tempStartDate);
      setTempStartDate(date);
    } else {
      setTempEndDate(date);
    }
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(currentMonth);
    
    const firstDay = new Date(year, month, 1);
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    const prevMonthDays = getDaysInMonth(new Date(year, month - 1, 1));
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i),
        isCurrentMonth: false,
        isDisabled: true
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        date,
        isCurrentMonth: true,
        isDisabled: false
      });
    }
    
    // Next month days
    const totalCells = 42; // 6 weeks
    for (let i = 1; days.length < totalCells; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
        isDisabled: true
      });
    }
    
    return days;
  };

  const days = generateCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="relative">
      <button
        onClick={() => setShowPicker(!showPicker)}
        className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <Calendar className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">
          {formatDate(startDate)} - {formatDate(endDate)}
        </span>
        <ChevronLeft className="w-4 h-4 text-gray-400" />
      </button>

      {showPicker && (
        <div className="absolute right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 w-96">
          <div className="p-4">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">Select Date Range</h3>
                <p className="text-sm text-gray-500">Choose start and end dates</p>
              </div>
              <button
                onClick={() => setShowPicker(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Selected Dates */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="border rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Start Date</p>
                <p className="font-medium">
                  {tempStartDate ? formatDate(tempStartDate) : 'Select date'}
                </p>
              </div>
              <div className="border rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">End Date</p>
                <p className="font-medium">
                  {tempEndDate ? formatDate(tempEndDate) : 'Select date'}
                </p>
              </div>
            </div>

            {/* Calendar */}
            <div className="mb-4">
              {/* Month Navigation */}
              <div className="flex justify-between items-center mb-4">
                <button
                  onClick={() => setCurrentMonth(
                    new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
                  )}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <span className="font-semibold">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <button
                  onClick={() => setCurrentMonth(
                    new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
                  )}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Week Days */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map(day => (
                  <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                    {day}
                  </div>
                ))}
              </div>

              {/* Days */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((day, index) => {
                  const isSelected = tempStartDate && 
                    day.date.toDateString() === tempStartDate.toDateString() ||
                    tempEndDate && 
                    day.date.toDateString() === tempEndDate.toDateString();
                  
                  const isInRange = tempStartDate && tempEndDate &&
                    day.date >= tempStartDate && day.date <= tempEndDate;
                  
                  return (
                    <button
                      key={index}
                      onClick={() => !day.isDisabled && handleDateClick(day.date)}
                      disabled={day.isDisabled}
                      className={`
                        w-10 h-10 rounded text-sm font-medium
                        ${day.isDisabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}
                        ${day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                        ${isSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
                        ${isInRange && !isSelected ? 'bg-blue-100 text-blue-900' : ''}
                      `}
                    >
                      {day.date.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quick Presets */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <button
                onClick={() => {
                  const end = new Date();
                  const start = new Date();
                  start.setDate(start.getDate() - 7);
                  setTempStartDate(start);
                  setTempEndDate(end);
                }}
                className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded"
              >
                Last 7 days
              </button>
              <button
                onClick={() => {
                  const end = new Date();
                  const start = new Date();
                  start.setDate(start.getDate() - 30);
                  setTempStartDate(start);
                  setTempEndDate(end);
                }}
                className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded"
              >
                Last 30 days
              </button>
              <button
                onClick={() => {
                  const end = new Date();
                  const start = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
                  setTempStartDate(start);
                  setTempEndDate(end);
                }}
                className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded"
              >
                This month
              </button>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 border-t pt-4">
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                disabled={!tempStartDate || !tempEndDate}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;